import { useEffect, useState } from 'react';
import useLocale from './localeHook';
import { suggestions } from '../search';

/**
 * `useDynamicSearchSuggestions` is a custom React hook that fetches dynamic search suggestions from the /suggestions API.
 * The search suggestions are based on the current locale and the specified count.
 *
 * @function
 * @module useDynamicSearchSuggestions
 * @param {number} count - The count of search suggestions to fetch.
 *
 * @returns {Array} An array of search suggestions. Each suggestion is an object with `searchTerm` and `urlPath` properties.
 * @returns {string} suggestion.searchTerm - The search term.
 * @returns {string} suggestion.urlPath - The URL path for the search term. This is a URL-encoded version of the search term, prefixed with 'w?q=' and suffixed with '&'.
 *
 * @example
 * const dynamicSearchSuggestions = useDynamicSearchSuggestions(4);
 */
export const useDynamicSearchSuggestions = () => {
  const [dynamicSearchSuggestions, setDynamicSearchSuggestions] = useState([]);
  const { country, language } = useLocale();

  useEffect(() => {
    if (country && language) {
      (async () => {
        try {
          const { searchTerms = [] } = await suggestions('', { country, language, count: 8 });

          const transformedData = searchTerms.map(({ searchText }) => ({
            searchTerm: searchText,
            urlPath: `w`,
          }));

          setDynamicSearchSuggestions(transformedData);
        } catch (error) {
          setDynamicSearchSuggestions([]);
        }
      })();
    }
  }, [country, language]);

  return dynamicSearchSuggestions;
};
